/**
 * Autofill the cart and submit the final order on staff intervention
 * (HITL-1551).
 *
 * WHY THIS IS THIN. It fills the CART, then calls the ordinary `sendOrder`.
 * There is no parallel submit path: the earlier draft of this ticket forked one
 * to dodge `sendOrder`'s `getValidCartItems` filter, and paid for it by having to
 * re-implement order-metadata assembly and by silently dropping
 * `dispatchOrderSideEffects` (coupon/loyalty registration and the COUPON_ADDED
 * metric), `populateOrderRequests` (so the returning POS `check` had no request
 * to attach to) and `sendOrderMetrics`. Filling the cart instead makes the items
 * valid, so the filter keeps them and all of that comes along for free.
 *
 * ON `sendOrder`'s `isAIAutoMode` GUARD. This path opts out of it explicitly,
 * with `bypassAIAutoMode`. The ticket needs every escalated car covered, "AI up
 * or down", and by the time staff take a car the AI is already suppressed on its
 * own side (`escalation_sent`), so there is no second order to race.
 *
 * An earlier version of this file claimed no bypass was needed, because a staff
 * intervention dispatches `sendAgentInterception` before submitting and that
 * clears the flag. The flag does usually end up false — but not for that reason,
 * and not reliably: `ai.slice.ts` clears it on EVERY interception type, and
 * `TTS_SENT` is one of them, emitted by ordinary AI-driven ordering with no agent
 * involvement (menuSlice.ts, useTTSMessage, usePOSCart, useReadback...). Relying
 * on it meant the submit worked by accident and would silently skip any car that
 * happened not to emit one.
 *
 * THE CALLER WAITS FOR THIS. `cancelOrder` defers `sendEndSession` and
 * `setFinalOrderSubmitted(true)` — which resets the cart and messages — until
 * this settles, or until a cap expires. That is what lets the outcome metric
 * below be both accurate (computed after the send) and deliverable (emitted
 * before the session ends); firing and forgetting gave you one or the other.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { cartActions } from './cartSlice';
import { orderActions, sendOrder } from './orderSlice';
import { buildCartAutofillSelections } from '../utils/cartAutofill';
import { convertToMapUsingName } from '../utils';
import logger from '../utils/logger';
import { messagingActions } from './messagingSlice';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';

/** Stable New Relic token — count on this, not on the prose. */
export const AUTOFILL_FINAL_ORDER_LOG_TOKEN = 'AUTOFILL_FINAL_ORDER';
export const AUTOFILL_FINAL_ORDER_METRIC = 'AUTOFILL_FINAL_ORDER_METRIC';
export const AUTOFILL_FINAL_ORDER_METRIC_SCHEMA_VERSION = '2.0';

/**
 * What the attempt did. There is no agent-facing surface on this path — the
 * session is torn down moments later — so this metric IS the surface, both in
 * New Relic (`module = 'HITL'`) and on the event stream.
 */
export enum AutofillFinalOrderOutcome {
  /** Slots were filled, and the order was handed to `sendOrder`. */
  filledAndSubmitted = 'filled_and_submitted',
  /** Nothing needed filling; the order was submitted as it stood. */
  submittedUnchanged = 'submitted_unchanged',
  /** At least one required slot had no fillable option. Submitted anyway —
   * `sendOrder` drops only the still-invalid items, so the rest of the cart
   * still reaches staff, which beats sending nothing. */
  submittedIncomplete = 'submitted_incomplete',
  /** `sendOrder` shipped nothing: every item still invalid, an empty cart, or a
   * rejected request. The order did NOT reach the POS. */
  notSubmitted = 'not_submitted',
  /** The kill switch is off. */
  disabled = 'disabled',
}

export interface IAutofillFinalOrderResult {
  outcome: AutofillFinalOrderOutcome;
  /** One `modGroup=item` entry per fill — the "fills are logged" criterion. */
  fills: string[];
  /** Required groups the ladder could not fill. */
  unfillable: string[];
}

export interface IAutofillFinalOrderArgs {
  /** The same handlers interim orders use, so a failed FINAL submit surfaces
   * through the menu-module error UI. Threaded from the call site rather than
   * rebuilt here. */
  menuUnificationOrderFailureCallback?: Function;
  menuUnificationOrderSuccessCallback?: Function;
}

export const autofillAndSubmitFinalOrder = createAsyncThunk<
  IAutofillFinalOrderResult,
  IAutofillFinalOrderArgs,
  { state: any }
>(
  'order/autofillAndSubmitFinalOrder',
  async (
    {
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
    },
    { dispatch, getState }
  ) => {
    const {
      config: {
        FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION,
        FEATURE_FLAG_SUPPRESS_UPSELL,
        FEATURE_FLAG_AUTO_COMBO,
        FEATURE_FLAG_BM_AUTO_COMBO,
      },
      menu: { brandMenu, modSymbolMapping, menuRes, unavailableItemsByName },
      cart: { cartItems },
      autoCombo: { autoCombinedCartItems },
      restaurant,
      sessionBoundary: { sessionId },
    } = getState();

    const restaurantCode =
      restaurant?.selectedRestaurantDetails?.restaurantCode || '';

    if (!FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION) {
      return {
        outcome: AutofillFinalOrderOutcome.disabled,
        fills: [],
        unfillable: [],
      };
    }

    // HITL's client-side auto-combo is legacy — combining moved to the backend,
    // so a combined item reaches HITL as an ordinary cart item and
    // `autoCombinedCartItems` stays empty. The fill therefore works on
    // `cart.cartItems`, which is both what `sendOrder` ships and the only map
    // `selectModifiers` can write.
    //
    // If that assumption is ever wrong the fill would silently miss the shipped
    // cart (a combined item's synthetic cartItemId is not in `cart.cartItems`,
    // so every selection is dropped), which is worth knowing about rather than
    // guarding a dead branch for.
    // ...and if it ever is populated, DO NOT FILL. Filling `cart.cartItems`
    // anyway would leave `fills` non-empty while `sendOrder` ships the combined
    // cart, so the outcome would report `filled_and_submitted` for a fill that
    // never reached the POS — the one failure this path must not produce.
    // Submitting unfilled is honest: the order still goes out, and the metric
    // says nothing was filled.
    // Mirrors `sendOrder`'s own ship condition (orderSlice.ts) EXACTLY, flags
    // included. Testing only for populated `autoCombinedCartItems` is not the
    // same test: with combined items present but both combo flags off,
    // `sendOrder` ships `cart.cartItems`, so skipping the fill would send the
    // unfilled cart and `getValidCartItems` would drop the incomplete line —
    // the precise failure this feature exists to prevent.
    const legacyAutoCombo =
      (FEATURE_FLAG_AUTO_COMBO ||
        (getMenuUnificationFeatureFlag() && FEATURE_FLAG_BM_AUTO_COMBO)) &&
      Object.values(autoCombinedCartItems ?? {}).length > 0;
    if (legacyAutoCombo) {
      logger.warn({
        message: `${AUTOFILL_FINAL_ORDER_LOG_TOKEN}: legacy auto-combined cart is populated; submitting without filling (see HITL-1556)`,
        sessionId,
        restaurantCode,
      });
    }

    const { selections, fills, unfillable } = buildCartAutofillSelections(
      legacyAutoCombo ? {} : cartItems,
      {
        brandMenuConstraints: convertToMapUsingName(
          brandMenu?.constraints ?? []
        ),
        modSymbolMapping: modSymbolMapping ?? {},
        posSettings: menuRes?.posSettings ?? {},
        suppressUpsellEnabled: FEATURE_FLAG_SUPPRESS_UPSELL,
      }
    );

    // MUST be set before the fill lands. Filling flips items from invalid to
    // valid, so `selectValidCartItems` changes and OrderReview's cart-change
    // effect (OrderReview.tsx:105) fires — sending an interim order right behind
    // our final one, and bumping `menuModuleRequestSeqId` so the final's own
    // callbacks are gated out as stale. `sendOrder` drops non-final sends while
    // this is set.
    dispatch(orderActions.setIsAutofillFinalInFlight(true));

    try {
      if (selections.length) {
        dispatch(
          cartActions.selectModifiers({ selections, unavailableItemsByName })
        );
      }

      // The cart is now as complete as it can be made. Everything from here is
      // the ordinary final-order path.
      // Cast: this thunk is typed `{ state: any }` (the store's RootState is not
      // importable here without a cycle), which loosens thunkAPI's dispatch past
      // what a nested thunk's signature wants.
      const sent = await (dispatch as any)(
        sendOrder({
          isFinal: true,
          // Every escalated car, whether or not the AI still holds auto mode.
          bypassAIAutoMode: true,
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );

      // OUTCOME IS DECIDED AFTER THE SEND, and from what the send actually did.
      // Reporting it beforehand meant a cart that shipped nothing — every item
      // still invalid, an empty cart, a rejected request — was recorded as
      // `submitted_*`. On a path whose only surface is this metric, that is a
      // success signal for a failure. It is safe to be late because the caller
      // holds the session open until this thunk settles (see the header); the
      // New Relic log would survive teardown regardless, being HTTP rather than
      // the websocket, but the event-stream copy would not.
      // "Did the final order pass the Menu Module layer" — that, and only that,
      // is what this feature succeeding means. `sendOrder` returns true when the
      // Menu Module accepted it.
      //
      // The previous signal counted `requestsOrder` growth, which is wrong on
      // the DEFAULT path: a successful final runs `finishOrderResetValues`,
      // which collapses that array to the single final request
      // (orderSlice.ts, HITL-1527). So the count did not grow and every
      // successful submit recorded `not_submitted` — the metric read backwards
      // for the common case.
      const shipped = sent?.payload === true;
      const outcome = !shipped
        ? AutofillFinalOrderOutcome.notSubmitted
        : unfillable.length
        ? AutofillFinalOrderOutcome.submittedIncomplete
        : fills.length
        ? AutofillFinalOrderOutcome.filledAndSubmitted
        : AutofillFinalOrderOutcome.submittedUnchanged;

      const result: IAutofillFinalOrderResult = { outcome, fills, unfillable };
      const logPayload = {
        message: `${AUTOFILL_FINAL_ORDER_LOG_TOKEN}: ${outcome}`,
        sessionId,
        restaurantCode,
        moreInfo: { fills, fillCount: fills.length, unfillable },
      };
      if (!shipped || unfillable.length) {
        logger.warn(logPayload);
      } else {
        logger.info(logPayload);
      }

      dispatch(
        messagingActions.sendInfo({
          data: {
            type: AUTOFILL_FINAL_ORDER_METRIC,
            schema_version: AUTOFILL_FINAL_ORDER_METRIC_SCHEMA_VERSION,
            outcome,
            fills,
            fill_count: fills.length,
            unfillable,
          },
        } as any)
      );

      return result;
    } finally {
      // The suppression window closes as soon as the final send has been made.
      // In `finally` so a throw cannot strand it on — leaving it set would mute
      // every interim order for the rest of the session.
      dispatch(orderActions.setIsAutofillFinalInFlight(false));
    }
  }
);
